// LazilyRenderNodes.jsx
import dagre from "dagre";
import { Box } from "@mui/material";
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import TextUpdaterNode from "../components/TextUpdaterNode";
import { useGetHierarchyQuery } from "../redux/organizationApi";

const nodeWidth = 350;
const nodeHeight = 200;
const DEFAULT_FOCUS_ID = "68df3d01f52c337448260ab9"; // fallback id you provided

// Tweak these to control how camera behaves
const MAX_ZOOM = 0.8;
const MIN_ZOOM = 0.15;
const VIEWPORT_PADDING = 0.12; // fraction of viewport used to fit bbox (0..1)
const TARGET_SCREEN_VERTICAL_FRACTION = 0.25; // target node placed ~25% from top

// Apply Dagre layout to nodes & edges
const getLayoutedElements = (nodes, edges, direction = "TB") => {
  if (!nodes?.length) return { nodes, edges };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 150,
    ranksep: 200,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges?.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return {
    nodes: layoutedNodes,
    edges: edges || [],
  };
};

export const LazilyRenderNodes = () => {
  const nodeTypes = useMemo(() => ({ custom: TextUpdaterNode }), []);

  // API
  const { data, isLoading, isError } = useGetHierarchyQuery();
  // React Flow instance
  const [rfInstance, setRfInstance] = useState(null);
  const focusedOnceRef = useRef(false);

  const onInit = useCallback((instance) => {
    setRfInstance(instance);
    // optional: initial viewport (keeps initial overall zoom reasonable)
    try {
      if (typeof instance.setViewport === "function") {
        instance.setViewport({ x: 0, y: 0, zoom: 0.35 });
      } else if (typeof instance.setCenter === "function") {
        instance.setCenter(0, 0, 0.35);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Helper: read target ID from URL (?focusId=...) or localStorage 'focusNodeId'
  const getTargetIdFromEnv = useCallback(() => {
    try {
      const url = new URL(window.location.href);
      const param = url.searchParams.get("focusId");
      if (param && String(param).trim()) return String(param).trim();
    } catch (e) {
      // ignore
    }
    const ls = window.localStorage.getItem("focusNodeId");
    if (ls && String(ls).trim()) return String(ls).trim();
    // fallback to default provided id
    return DEFAULT_FOCUS_ID;
  }, []);

  // Build layouted nodes & edges when data changes
  const { nodes, edges } = useMemo(() => {
    if (!data?.nodes || !data?.edges) return { nodes: [], edges: [] };
    return getLayoutedElements(data?.nodes, data.edges, "TB");
  }, [data]);

  // helper: get node by id robustly
  const findNodeById = useCallback((nodesArray, id) => {
    if (!Array.isArray(nodesArray) || !id) return null;
    const idStr = String(id);
    return nodesArray.find((n) => {
      if (!n) return false;
      if (String(n.id) === idStr) return true;
      if (String(n._id) === idStr) return true;
      if (String(n.data?.nodeId) === idStr) return true;
      if (String(n.data?._id) === idStr) return true;
      return false;
    });
  }, []);

  // focus group around target: target + children + parents + siblings (up to a small set)
  const focusGroupAroundTarget = useCallback(
    (instance, nodesArray, targetId, opts = { duration: 600 }) => {
      if (!instance || !Array.isArray(nodesArray) || !targetId) return false;
      const target = findNodeById(nodesArray, targetId);
      if (!target) return false;

      // collect neighbor ids
      const ids = new Set();
      ids.add(String(target.id));

      // children (node.children may be array of ids)
      const childIds = Array.isArray(target.children)
        ? target.children.map(String)
        : [];
      childIds.forEach((c) => ids.add(c));

      // parent(s)
      const parentIds = Array.isArray(target.parent)
        ? target.parent.map(String)
        : target.parent
        ? [String(target.parent)]
        : [];
      parentIds.forEach((p) => ids.add(p));

      // siblings: parent's children (first parent only for simplicity)
      if (parentIds.length > 0) {
        const parentNode = findNodeById(nodesArray, parentIds[0]);
        if (parentNode && Array.isArray(parentNode.children)) {
          parentNode.children.slice(0, 10).forEach((s) => ids.add(String(s)));
        }
      }

      // produce array of nodes to include (preserve target)
      const groupNodes = [target];
      for (const id of ids) {
        if (String(id) === String(target.id)) continue;
        const n = findNodeById(nodesArray, id);
        if (n) groupNodes.push(n);
        if (groupNodes.length >= 8) break; // limit size so bbox isn't huge
      }

      // build bounding box including node widths/heights
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (const n of groupNodes) {
        // robust position
        let pos = n.position;
        if (!pos || typeof pos.x !== "number" || typeof pos.y !== "number") {
          const fx =
            n?.possssss?.xdata ?? (n.position ? n.position.x : undefined);
          const fy =
            n?.possssss?.ydata ?? (n.position ? n.position.y : undefined);
          if (typeof fx === "number" && typeof fy === "number")
            pos = { x: fx, y: fy };
        }
        if (!pos) continue;
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + nodeWidth);
        maxY = Math.max(maxY, pos.y + nodeHeight);
      }

      if (!isFinite(minX) || !isFinite(minY)) {
        // fallback: center on single node
        try {
          if (typeof instance.setCenter === "function") {
            instance.setCenter(
              target.position?.x ?? 0,
              target.position?.y ?? 0,
              Math.min(MAX_ZOOM, 0.6),
              { duration: opts.duration }
            );
          } else {
            instance.setViewport(
              {
                x: -(target.position?.x ?? 0) + window.innerWidth / 2,
                y: -(target.position?.y ?? 0) + window.innerHeight / 2,
                zoom: Math.min(MAX_ZOOM, 0.6),
              },
              { duration: opts.duration }
            );
          }
        } catch (e) {
          // ignore
        }
        return false;
      }

      const bboxWidth = Math.max(1, maxX - minX);
      const bboxHeight = Math.max(1, maxY - minY);

      // compute zoom to fit bbox into viewport with padding
      const usableW = window.innerWidth * VIEWPORT_PADDING;
      const usableH = window.innerHeight * VIEWPORT_PADDING;
      let scaleX = usableW / bboxWidth;
      let scaleY = usableH / bboxHeight;
      let zoom = Math.min(scaleX, scaleY);

      // clamp zoom
      zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));

      // compute center of bbox in graph coords
      const centerX = minX + bboxWidth / 2;
      const centerY = minY + bboxHeight / 2;

      // We want the target to appear near the top (TARGET_SCREEN_VERTICAL_FRACTION).
      // Compute desired screen coords and translate to viewport x/y:
      const desiredScreenX = window.innerWidth / 2; // center horizontally
      const desiredScreenY =
        window.innerHeight * TARGET_SCREEN_VERTICAL_FRACTION;

      // viewport.x/y satisfy: screenX = nodeX * zoom + viewport.x
      // so viewport.x = desiredScreenX - nodeX * zoom
      // Choose node used for vertical placement as the target node center
      const targetPos = target.position ?? { x: 0, y: 0 };
      const targetCenterX = targetPos.x + nodeWidth / 2;
      const targetCenterY = targetPos.y + nodeHeight / 2;

      const viewportX = desiredScreenX - targetCenterX * zoom;
      const viewportY = desiredScreenY - targetCenterY * zoom;

      try {
        if (typeof instance.setViewport === "function") {
          instance.setViewport(
            { x: viewportX, y: viewportY, zoom },
            { duration: opts.duration }
          );
        } else if (typeof instance.setCenter === "function") {
          // fallback: center the bbox center (can't offset target to top precisely)
          instance.setCenter(centerX, centerY, zoom, {
            duration: opts.duration,
          });
        }
        return true;
      } catch (err) {
        console.error("focusGroupAroundTarget error:", err);
        return false;
      }
    },
    [findNodeById]
  );

  // effect: focus once on first mount when rfInstance + nodes ready
  useEffect(() => {
    if (focusedOnceRef.current) return;
    if (!rfInstance) return;
    if (!nodes || !nodes.length) return;

    const targetId = getTargetIdFromEnv();
    if (!targetId) {
      focusedOnceRef.current = true;
      return;
    }

    const ok = focusGroupAroundTarget(rfInstance, nodes, targetId, {
      duration: 600,
    });
    if (ok) {
      console.log(`[LazilyRenderNodes] Focused group around id=${targetId}`);
    } else {
      console.warn(
        `[LazilyRenderNodes] Could not focus group around id=${targetId}; falling back to single focus.`
      );
      // fallback: simple single-node focus
      try {
        const node = findNodeById(nodes, targetId);
        if (node) {
          if (typeof rfInstance.setCenter === "function") {
            rfInstance.setCenter(
              node.position?.x ?? 0,
              node.position?.y ?? 0,
              Math.min(MAX_ZOOM, 0.5),
              { duration: 600 }
            );
          } else {
            rfInstance.setViewport(
              {
                x: -(node.position?.x ?? 0) + window.innerWidth / 2,
                y: -(node.position?.y ?? 0) + window.innerHeight / 2,
                zoom: Math.min(MAX_ZOOM, 0.5),
              },
              { duration: 600 }
            );
          }
        }
      } catch (e) {
        // ignore
      }
    }

    focusedOnceRef.current = true;
  }, [
    rfInstance,
    nodes,
    focusGroupAroundTarget,
    getTargetIdFromEnv,
    findNodeById,
  ]);

  if (isLoading) {
    return (
      <Box
        height={"100vh"}
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        Loading hierarchy...
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        height={"100vh"}
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No data available.
      </Box>
    );
  }

  return (
    <Box height={"100vh"} width="100vw" border="1px solid black">
      <ReactFlow
        onInit={onInit}
        maxZoom={MAX_ZOOM}
        minZoom={MIN_ZOOM}
        defaultViewport={{ zoom: 0.35 }}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        panOnDrag
        panOnScroll
        zoomOnScroll
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        selectionOnDrag={false}
        proOptions={{ hideAttribution: true }}
        // onlyRenderVisibleElements={true}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </Box>
  );
};
